import { useEffect, useState } from "react";
import { DataTable, DataTableField, DataTableCustomActions } from "../../../components/data/DataTable";
import Button from "../../../components/base/Button";
import Modal from "../../../components/base/Modal";
import Icon from "../../../components/base/Icon";
import FlexRow from "../../../components/base/FlexRow";
import { toastSuccess } from "../../../components/base/Toast";
import { API_ROUTES } from "../../../core/Constants";
import { toPrettyDate, useHub } from "../../../core/Global";
import UserGroup from "../models/UserGroup";
import AddMembersDrawer, { PickItem } from "./AddMembersDrawer";


interface IMember {
    id?: string
    name?: string
    username?: string
    userType?: string
}

interface IProps {
    model: UserGroup
}

export default function UserGroupUsers(props: IProps) {

    const hub = useHub();

    const [refresh, refreshSet] = useState<boolean>(false);
    const [addOpen, addOpenSet] = useState<boolean>(false);

    const [pool, poolSet] = useState<PickItem[]>([]);
    const [assigned, assignedSet] = useState<string[]>([]);

    const [removeModal, removeModalSet] = useState<boolean>(false);
    const [pendingRemove, pendingRemoveSet] = useState<{ ids: string[], label: string }>();

    useEffect(() => {
        if (refresh) setTimeout(() => refreshSet(false), 800);
    }, [refresh])

    const fetchPool = () => {
        hub.Get(API_ROUTES.UsersGroups + "members-all/" + props.model.id, res => {
            if (res.success) {
                const all: IMember[] = res.data.all ?? [];
                poolSet(all.filter(u => u.id).map(u => ({ id: u.id!, title: u.name, subtitle: "@" + u.username })));
                assignedSet(res.data.assigned ?? []);
            }
        });
    }

    useEffect(() => {
        fetchPool();
    }, [props.model.id])

    const afterChange = () => {
        refreshSet(true);
        fetchPool();
    }


    const addMany = (userIds: string[]) => {
        hub.Post(API_ROUTES.UsersGroups + "add-members/" + props.model.id, userIds, res => {
            if (res.success) {
                toastSuccess("Users have been added to the group!");
                afterChange();
            }
        });
    }

    const askRemoveSingle = (data: any) => {
        pendingRemoveSet({ ids: [data.id], label: data.fullName });
        removeModalSet(true);
    }

    const askRemoveMany = (rows: any[]) => {
        const ids = (rows ?? []).map(r => r.id).filter(Boolean);
        if (ids.length == 0) return;
        pendingRemoveSet({ ids, label: `${ids.length} user${ids.length > 1 ? "s" : ""}` });
        removeModalSet(true);
    }

    const doRemove = (callback?: (res?: any) => void) => {
        const ids = pendingRemove?.ids ?? [];
        hub.Post(API_ROUTES.UsersGroups + "remove-members/" + props.model.id, ids, res => {
            if (res.success) {
                toastSuccess("Users have been removed from the group!");
                afterChange();
            }
            if (callback) callback(res);
        });
    }


    const CreatedItem = (data: any) => (
        <div>
            <div className="d-flex align-items-center">
                {data.groupJoinedAt ? toPrettyDate(data.groupJoinedAt) : <span className="text-muted">—</span>}
            </div>
            {
                data.groupJoinedByName &&
                <FlexRow className="fs-8 mt-1">
                    <Icon icon="user" className="me-2" size={10} type="solid" />
                    <div className="fs-8">By: {data.groupJoinedByName}</div>
                </FlexRow>
            }
        </div>
    )

    const RemoveAction = (data: any) => (
        <div className="menu-item">
            <a href="#" className="menu-link px-3 text-danger"
                onClick={e => {
                    e.preventDefault();
                    askRemoveSingle(data);
                }}>
                <Icon icon="user-xmark" className="me-2" />
                Remove from group
            </a>
        </div>
    )


    return (
        <>
            <div className="card">
                <div className="card-header border-0">
                    <div className="card-title m-0">
                        <h3 className="fw-bolder m-0">Users in Group</h3>
                    </div>
                    <div className="card-action">
                        <Button icon="plus" text="Add User" onClick={() => addOpenSet(true)} />
                    </div>
                </div>
                <div className="card-body border-top pt-2">
                    <DataTable noCard preventCreate objectName="User"
                        apiRoute={API_ROUTES.Users + "ingroup/" + props.model.id}
                        refresh={refresh}
                        groupActions={[{
                            title: "Remove from group",
                            icon: "circle-minus",
                            iconClass: "text-danger",
                            action: askRemoveMany
                        }]}>

                        <DataTableField title="Name" dataValueName="fullName" />
                        <DataTableField title="Username" dataValueName="username" />
                        <DataTableField title="Type" dataValueName="userTypeName" />
                        <DataTableField title="Added">
                            <CreatedItem />
                        </DataTableField>

                        <DataTableCustomActions>
                            <RemoveAction />
                        </DataTableCustomActions>

                    </DataTable>
                </div>
            </div>

            {
                removeModal && pendingRemove &&
                <Modal id="modal-remove-group-member"
                    title="Remove from Group"
                    buttonText="Remove"
                    open visible staticBackdrop
                    onClose={() => {
                        removeModalSet(false);
                        pendingRemoveSet(undefined);
                    }}
                    onSubmit={(body, callback) => {
                        doRemove(res => {
                            // close via the modal's own script (removes the backdrop),
                            // then onClose unmounts it — never unmount directly.
                            if (res?.success) window.closeModal("modal-remove-group-member");
                            if (callback) callback(res);
                        });
                    }}>
                    <div className="my-5">
                        Are you sure you want to remove <b>{pendingRemove.label}</b> from this group?
                    </div>
                </Modal>
            }

            {
                addOpen &&
                <AddMembersDrawer
                    drawerId="drawer-add-group-members"
                    icon="user"
                    drawerTitle="Add Users to Group"
                    addLabel="Add User"
                    memberLabel="Member"
                    items={pool}
                    selectedIds={assigned}
                    onAddMany={addMany}
                    onClose={() => addOpenSet(false)} />
            }
        </>
    )
}
